/**
 * Authentication Store — Product Management Portal
 *
 * Manages user authentication state, tokens, and user information.
 * Mirrors the auth store from tms-app-soho.
 *
 * Token refresh:
 *   • Proactive: schedules a refresh ~60 s before accessToken expiry
 *   • Reactive:  401 interceptor in api.service.ts calls refreshAccessToken()
 */
import { defineStore } from 'pinia';
import { isAxiosError } from 'axios';
import type { BaseUser, TotpInitiateResponse, MfaStatus } from '@tms-phoenix/tms-types';
import { AUTH_SESSION_EXP_COOKIE } from '@tms-phoenix/tms-types';
import { computeDiff, hasIdleTimeoutElapsed, clearLastActivityTimestamp } from '@tms-phoenix/ui-vue';
import { getApiClient } from '@/core/services/api.service';
import { logAuditEvent } from '@/core/services/audit-service';
import { API_ROUTES, setAuthContext, clearAuthContext } from '@/core/config';
import type { AuthLoginResponse, AuthLoginChallenge, AuthResetPasswordResponse } from '@/modules/auth/types';
import type { UserDetailsResponse } from '@/modules/auth/services/auth.service';
import { fetchUserDetails } from '@/modules/auth/services/auth.service';
import { getApiErrorMessage } from '@/core/utils/api-error.util';
import { router } from '@/router/instance';

// ── Proactive refresh timer ─────────────────────────────────────────────────
let _refreshTimerId: ReturnType<typeof setTimeout> | null = null;

/** In-flight refresh promise mutex — prevents concurrent refresh calls. */
let _refreshPromise: Promise<boolean> | null = null;

// ── Transient auth-failure retry (SV-563) ───────────────────────────────────
let _sessionRetryTimerId: ReturnType<typeof setTimeout> | null = null;
let _sessionRetryAttempt = 0;

/** Statuses that mean the session itself is invalid — the only ones that should log the user out. */
const SESSION_INVALID_STATUSES = new Set([400, 401, 403]);

// Keep the retry policy below in sync with tms-app-soho and tms-app-donor auth.store.ts.

/** Backoff for 5xx / network errors — covers a Wi-Fi roam or a rolling deploy (~3 min total). */
const SESSION_RETRY_DELAYS_MS = [5_000, 15_000, 45_000, 120_000];
/** /auth/refresh blocks for up to its 15-minute rate-limit window; never wait longer than that. */
const MAX_RETRY_AFTER_MS = 15 * 60_000;
/** ±25 % spread so browsers that failed together don't all retry at the same instant. */
const RETRY_JITTER = 0.25;

/** Spread proactive refreshes over this window so tabs sharing an expiry don't all refresh at once. */
const REFRESH_SPREAD_SEC = 30;
/**
 * After a 429 with Retry-After, no /auth/refresh before this time (epoch ms) — enforced in
 * refreshAccessToken() so every caller (401 interceptor, guard, timers) honours it.
 */
let _refreshNotBefore = 0;

/** What _scheduleSessionRetry() scheduled — `null` when retries are exhausted. */
type ScheduledSessionRetry = { retryAttempt: number; retryInMs: number } | null;

/** A 429's Retry-After in ms (capped at MAX_RETRY_AFTER_MS), or `null` when absent or not a 429. */
function retryAfterMs(err: unknown): number | null {
  if (!isAxiosError(err) || err.response?.status !== 429) return null;
  const retryAfterSec = Number(err.response.headers?.['retry-after']);
  return Number.isFinite(retryAfterSec) && retryAfterSec > 0 ? Math.min(retryAfterSec * 1000, MAX_RETRY_AFTER_MS) : null;
}

/**
 * SV-563: how long to wait before retrying /auth/session-check or /auth/refresh after a
 * transient failure (429 rate limit, 5xx, network error). The user stays signed in either way.
 *
 * @param err     The error thrown by the failed request (usually an AxiosError).
 * @param attempt 0 for the first retry, incremented on each consecutive failure.
 * @returns Delay in ms before retrying, or `null` to stop retrying.
 */
function getSessionRetryDelayMs(err: unknown, attempt: number): number | null {
  // Give up after 4 attempts — the user stays signed in; the 401 → refresh path still applies.
  if (attempt >= SESSION_RETRY_DELAYS_MS.length) return null;

  // 429 with Retry-After: the server says exactly when a retry can succeed. Only add
  // jitter *after* that point — retrying earlier would just be rejected again.
  const retryAfter = retryAfterMs(err);
  if (retryAfter !== null) {
    return Math.round(retryAfter + Math.random() * 2_000);
  }

  // 5xx, network errors, or a 429 without Retry-After: increasing gaps with jitter.
  const base = SESSION_RETRY_DELAYS_MS[attempt];
  return Math.round(base * (1 - RETRY_JITTER + Math.random() * 2 * RETRY_JITTER));
}

/** HTTP status for log lines, or 'network' when the request never got a response. */
function describeAuthFailure(err: unknown): string {
  return String((isAxiosError(err) && err.response?.status) || 'network');
}

// ── Session-expiry marker cookie (SV-563) ───────────────────────────────────
// Keep in sync with tms-app-soho and tms-app-donor auth.store.ts.

/** Another tab's refresh only counts if it moved the expiry forward by more than this. */
const MARKER_NEWER_THRESHOLD_SEC = 30;
/**
 * The marker is page-writable, so reject values further ahead than any real access token can
 * last (Cognito's maximum access-token validity is 24 h) — a tampered value then behaves like
 * a missing marker (the /auth/session-check fallback) instead of skewing refresh timing.
 */
const MAX_MARKER_AHEAD_SEC = 24 * 60 * 60;
/** Whether this page has ever seen a marker — a missing one only means "session ended" if so. */
let _sessionMarkerSeen = false;
let _visibilityListenerRegistered = false;

/**
 * Read the readable `tms_session_exp` marker the backend sets alongside the HttpOnly access
 * cookie (same lifetime). Returns its expiry in epoch seconds, or `null` when it's absent,
 * malformed, already past, or implausibly far ahead. A UI timing hint only — never used for
 * authorization.
 */
function readSessionExpCookie(): number | null {
  const prefix = `${AUTH_SESSION_EXP_COOKIE}=`;
  const entry = document.cookie.split('; ').find((c) => c.startsWith(prefix));
  const exp = Number(entry?.slice(prefix.length));
  const nowSec = Math.floor(Date.now() / 1000);
  if (!Number.isInteger(exp) || exp <= nowSec || exp > nowSec + MAX_MARKER_AHEAD_SEC) return null;
  _sessionMarkerSeen = true;
  return exp;
}

/**
 * Extract `exp` (Unix seconds) from a JWT for refresh-scheduling purposes.
 * Does NOT verify the token signature — use only as a UI hint (proactive
 * refresh timer).  Never use decoded claims for authorization decisions.
 */
function decodeJwtExp(token: string): number | null {
  try {
    let payload = token.split('.')[1];
    if (!payload) return null;
    payload = payload.replace(/-/g, '+').replace(/_/g, '/');
    while (payload.length % 4 !== 0) payload += '=';
    const json = JSON.parse(atob(payload));
    return typeof json.exp === 'number' ? json.exp : null;
  } catch {
    return null;
  }
}

const computeAuditDiff = computeDiff;

interface LoginCredentials {
  email: string;
  password: string;
}

interface AuthState {
  user: BaseUser | null;
  userDetails: UserDetailsResponse | null;
  accessToken: string | null;
  refreshToken: string | null;
  /**
   * SV-563: access-token expiry (epoch seconds) reported by GET /auth/session-check on page
   * reload, when no in-memory accessToken exists. In-memory only — never persisted.
   */
  sessionExpiresAt: number | null;
  username: string | null;
  staffId: string | null;
  hospitalId: string | null;
  tenantId: string | null;
  roles: string | null;
  parsedRoles: string[];
  defaultLanguage: string | null;
  timeZone: string | null;
  challengeData: AuthLoginChallenge['challenge'] | null;
  /** True when login succeeded but MFA has not been configured yet. */
  mfaSetupRequired: boolean;
  /** Which MFA challenge type is pending after login. */
  mfaChallengeType: 'SOFTWARE_TOKEN_MFA' | 'EMAIL_OTP' | null;
  /** QR code data from POST /mfa/setup/totp/initiate. */
  totpQrData: TotpInitiateResponse | null;
  /** Temporary access token from SELECT_MFA_TYPE challenge, used to authenticate MFA setup calls. */
  mfaSetupAccessToken: string | null;
  /** Email the user typed at login — fallback username when challengeParameters lacks USER_ID_FOR_SRP. */
  pendingEmail: string | null;
  isLoading: boolean;
  error: string | null;
  /** Error code returned by the API on login failure (e.g. 'USER_DISABLED'). */
  loginErrorCode: string | null;
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    user: null,
    userDetails: null,
    accessToken: null,
    refreshToken: null,
    sessionExpiresAt: null,
    username: null,
    staffId: null,
    hospitalId: null,
    tenantId: null,
    roles: null,
    parsedRoles: [],
    defaultLanguage: null,
    timeZone: null,
    challengeData: null,
    mfaSetupRequired: false,
    mfaChallengeType: null,
    totpQrData: null,
    mfaSetupAccessToken: null,
    pendingEmail: null,
    isLoading: false,
    error: null,
    loginErrorCode: null,
  }),

  getters: {
    isAuthenticated: (state): boolean => !!state.user,
    /**
     * SV-563: access-token expiry (epoch seconds). Prefers the in-memory token (set after
     * login/refresh in this page load) over the session-check value from page reload.
     */
    sessionExp: (state): number | null =>
      state.accessToken ? decodeJwtExp(state.accessToken) : state.sessionExpiresAt,
    /** SV-563: logged in AND the access-token expiry is known (login, refresh, or session-check). */
    hasActiveSession(): boolean {
      return this.isAuthenticated && this.sessionExp !== null;
    },
    userRole: (state): string | undefined => state.user?.role,
    userName: (state): string | undefined => state.user?.name || state.user?.email,
    userEmail: (state): string | undefined => state.user?.email,
  },

  actions: {
    /**
     * Shared post-authentication flow used by both login() and completeNewPasswordChallenge().
     */
    async _finishLogin(params: {
      accessToken: string;
      refreshToken: string;
      loginUser: { username: string; tenant_id?: string; hospital_id?: string; roles?: string; default_language?: string; time_zone?: string };
      fallbackEmail: string;
    }): Promise<boolean> {
      const { accessToken, refreshToken, loginUser, fallbackEmail } = params;
      const { username, tenant_id: tenantId, hospital_id: hospitalId, roles: rolesRaw, default_language: defaultLang, time_zone: timeZone } = loginUser;

      // ── Parse roles safely (JSON array string OR comma-separated string) ─
      // rolesRaw may be undefined for CP users when custom:roles is not injected
      // by the Pre-Token Lambda (e.g. first-time password reset on a brand-new account).
      let parsedRoles: string[] = [];
      if (rolesRaw) {
        try {
          const parsed = JSON.parse(rolesRaw);
          if (Array.isArray(parsed)) {
            parsedRoles = parsed.filter((r): r is string => typeof r === 'string');
          }
        } catch {
          // Not JSON — treat as comma-separated string (e.g. "ADMIN,PRODUCTION_SUPPORT")
          parsedRoles = rolesRaw
            .split(',')
            .map((r) => r.trim())
            .filter((r) => r.length > 0);
        }
      }

      // ── Reset permissions store for the new user ────────────────────
      // This ensures cached permissions from a previous login don't carry over.
      // Permissions will be re-fetched immediately after login completes.
      try {
        const permissionsStore = (await import('@/stores/permissions.store')).usePermissionsStore();
        permissionsStore.reset();
      } catch (_err) {
        // Silently ignore if permissions reset fails
      }

      // ── Persist login-derived data ────────────────────────────────────
      this.accessToken = accessToken;
      this.refreshToken = refreshToken;
      this.username = username;
      this.tenantId = tenantId ?? null;
      this.hospitalId = hospitalId ?? null;
      this.roles = rolesRaw ?? null;
      this.parsedRoles = parsedRoles;
      this.defaultLanguage = defaultLang ?? null;
      this.timeZone = timeZone ?? null;

      // ── Fetch full user details (GET /users/:username) ────────────────
      let staffId = '';
      let userDetails: UserDetailsResponse | null = null;
      const resolvedTenantId = tenantId ?? hospitalId;

      if (resolvedTenantId) {
        const maxRetries = 2;
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          try {
            userDetails = await fetchUserDetails(username, resolvedTenantId);
            staffId = userDetails.sub ?? '';
            if (userDetails.hospital_id) this.hospitalId = userDetails.hospital_id;
            if (userDetails.tenant_id) this.tenantId = userDetails.tenant_id;
            break;
          } catch (_detailsErr) {
            if (attempt < maxRetries) {
              await new Promise((r) => setTimeout(r, 1000));
            }
            // Silently continue on final retry failure — staffId will use fallback
          }
        }
      }

      // Fallback: use the Cognito sub (username) as staffId when user
      // details could not be fetched (e.g. tenant_id missing from JWT).
      if (!staffId) {
        staffId = username;
      }

      this.staffId = staffId;
      this.userDetails = userDetails;

      // ── Build BaseUser ────────────────────────────────────────────────
      const now = new Date().toISOString();
      const displayName = userDetails
        ? ((userDetails.first_name ?? '') + ' ' + (userDetails.last_name ?? '')).trim() || fallbackEmail
        : fallbackEmail;

      // Skip DEFAULT (a stale/placeholder assignment) in favour of a real role
      // assigned elsewhere in the array, regardless of array order.
      const primaryRole = (parsedRoles.find((r) => r.toUpperCase() !== 'DEFAULT') ?? parsedRoles[0] ?? '').toLowerCase();
      const ALLOWED_ROLES = ['user', 'admin', 'viewer'] as const;
      const mappedRole = ALLOWED_ROLES.includes(primaryRole as (typeof ALLOWED_ROLES)[number])
        ? (primaryRole as BaseUser['role'])
        : 'user';

      const user: BaseUser = {
        id: staffId || username,
        email: userDetails?.email ?? fallbackEmail,
        name: displayName,
        role: mappedRole,
        createdAt: userDetails?.created_at ?? now,
        updatedAt: now,
      };

      this.user = user;

      // ── Sync mutable config values ────────────────────────────────────
      setAuthContext({
        staffId,
        hospitalId: this.hospitalId || undefined,
        tenantId: this.tenantId || undefined,
      });

      // Schedule proactive token refresh
      this._scheduleProactiveRefresh();

      // ── Fetch role-module-matrix permissions (fire-and-forget) ────────
      // Permissions are used to control module visibility in the UI.
      // This is non-blocking: if the fetch fails, the app continues to work.
      try {
        const permissionsStore = (await import('@/stores/permissions.store')).usePermissionsStore();
        permissionsStore.fetchPermissions().catch(() => {
          // Non-fatal: permission-gated UI will update when fetch succeeds
        });
      } catch (_err) {
        // Silently ignore if permissions fetch couldn't be triggered
      }

      return true;
    },

    /**
     * Authenticate via the onboarding service login endpoint.
     *
     * POST /onboarding/api/v1/auth/login
     */
    async login(credentials: LoginCredentials): Promise<boolean> {
      this.isLoading = true;
      this.error = null;
      this.loginErrorCode = null;
      this.challengeData = null;

      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditResourceId: string | undefined;
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      const auditNewValue = {
        email: credentials.email,
      };

      try {
        const api = getApiClient();

        const loginResponse = await api.post<AuthLoginResponse>(
          `${API_ROUTES.ONBOARDING}/auth/login`,
          { email: credentials.email, password: credentials.password },
        );

        const loginData = loginResponse.data;

        if (loginData.type === 'challenge') {
          this.challengeData = loginData.challenge;
          if (
            loginData.challenge.challengeName === 'SOFTWARE_TOKEN_MFA' ||
            loginData.challenge.challengeName === 'EMAIL_OTP'
          ) {
            this.mfaChallengeType = loginData.challenge.challengeName;
          }
          // Capture setup token issued alongside SELECT_MFA_TYPE for MFA enrollment calls
          this.mfaSetupAccessToken = (loginData as AuthLoginChallenge).access_token ?? null;
          this.pendingEmail = credentials.email;
          // Always persist mfaSetupRequired from the challenge response (required MFA pool)
          if (loginData.mfaSetupRequired) this.mfaSetupRequired = true;

          // First-time MFA setup: /login returns full tokens with SELECT_MFA_TYPE.
          if (
            loginData.challenge.challengeName === 'SELECT_MFA_TYPE' &&
            (loginData as AuthLoginChallenge).refresh_token &&
            (loginData as AuthLoginChallenge).user
          ) {
            const challengeData = loginData as AuthLoginChallenge;
            await this._finishLogin({
              accessToken: challengeData.access_token!,
              refreshToken: challengeData.refresh_token!,
              loginUser: challengeData.user!,
              fallbackEmail: credentials.email,
            });
            this.mfaSetupRequired = true;
          }

          // mfaSetupRequired: skip MFA method-selection screen by auto-selecting EMAIL_OTP.
          // Fires for both token-bearing and token-less SELECT_MFA_TYPE responses.
          if (
            loginData.challenge.challengeName === 'SELECT_MFA_TYPE' &&
            loginData.mfaSetupRequired
          ) {
            const username = loginData.challenge.challengeParameters?.USER_ID_FOR_SRP ?? credentials.email;
            const autoRespondResponse = await api.post<AuthLoginResponse>(
              `${API_ROUTES.ONBOARDING}/auth/respond-challenge`,
              {
                challengeName: 'SELECT_MFA_TYPE',
                session: loginData.challenge.session,
                username,
                answer: 'EMAIL_OTP',
              },
            );
            const autoRespondData = autoRespondResponse.data;
            if (autoRespondData.type === 'challenge') {
              this.challengeData = autoRespondData.challenge;
              if (autoRespondData.challenge.challengeName === 'EMAIL_OTP') {
                this.mfaChallengeType = 'EMAIL_OTP';
              }
            }
          }

          auditStatus = 'SUCCESS';
          auditResourceId = loginData.challenge.challengeParameters?.USER_ID_FOR_SRP;
          this.isLoading = false;
          return false;
        }

        if (loginData.type !== 'authenticated') {
          throw new Error(`Unexpected auth response type: ${(loginData as { type: string }).type}`);
        }

        const {
          access_token: accessToken,
          refresh_token: refreshToken,
          user: loginUser,
        } = loginData.tokens;

        const success = await this._finishLogin({
          accessToken,
          refreshToken,
          loginUser,
          fallbackEmail: credentials.email,
        });

        if (success) {
          auditStatus = 'SUCCESS';
          auditResourceId = this.staffId ?? undefined;
          this.mfaSetupRequired = loginData.mfaSetupRequired === true;
        } else {
          auditErrorCode = 'LOGIN_FINISH_FLOW_FAILED';
          auditErrorMessage = this.error ?? 'Login post-auth flow failed';
        }

        return success;
      } catch (err) {
        this.error = getApiErrorMessage(err, 'Login failed');
        auditStatus = 'FAILURE';
        auditErrorCode = 'LOGIN_FAILED';
        auditErrorMessage = this.error ?? 'Login failed';
        if (isAxiosError(err)) {
          const apiError = (err.response?.data as { error?: unknown } | undefined)?.error;
          if (apiError && typeof apiError === 'object' && 'code' in apiError) {
            this.loginErrorCode = (apiError as { code: string }).code;
            auditErrorCode = this.loginErrorCode ?? 'LOGIN_FAILED';
          } else {
            auditErrorCode = typeof apiError === 'string' ? apiError : err.code ?? 'LOGIN_FAILED';
          }
        }
        return false;
      } finally {
        this.isLoading = false;
        try {
          logAuditEvent({
            eventCategory: 'AUTH',
            eventType: 'LOGIN',
            eventStatus: auditStatus,
            resourceType: 'users',
            resourceId: auditResourceId,
            isPhiAccess: false,
            oldValue: undefined,
            newValue: auditNewValue,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch {
          // Audit must never block the flow
        }
      }
    },

    /**
     * Complete the NEW_PASSWORD_REQUIRED challenge.
     *
     * POST /onboarding/api/v1/auth/respond-challenge
     */
    async completeNewPasswordChallenge(newPassword: string): Promise<boolean> {
      if (!this.challengeData) {
        this.error = 'No pending challenge. Please log in again.';
        return false;
      }

      this.isLoading = true;
      this.error = null;

      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditResourceId: string | undefined;
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      let auditOldValue: unknown | undefined;
      let auditNewValue: unknown | undefined;

      const existingSnapshot = this.challengeData as unknown as Record<string, unknown>;

      try {
        const api = getApiClient();
        const { challengeName, session, challengeParameters } = this.challengeData;
        // USER_ID_FOR_SRP is the Cognito username — may be email or UUID.
        const username = challengeParameters?.USER_ID_FOR_SRP ?? this.pendingEmail ?? '';
        if (!username) {
          this.challengeData = null;
          this.error = 'Session data is incomplete. Please log in again.';
          return false;
        }
        const requestBody = {
          challengeName,
          session,
          username,
          passwordUpdated: true,
        };

        const diff = computeAuditDiff(existingSnapshot, requestBody as unknown as Record<string, unknown>);
        auditOldValue = diff.oldValue;
        auditNewValue = diff.newValue;

        const resetResponse = await api.post<AuthLoginResponse | AuthResetPasswordResponse>(
          `${API_ROUTES.ONBOARDING}/auth/respond-challenge`,
          { challengeName, session, username, newPassword },
        );

        const resetData = resetResponse.data;

        let accessToken: string;
        let refreshToken: string;
        let loginUser: { username: string; tenant_id?: string; hospital_id?: string; roles?: string; default_language?: string; time_zone?: string };

        if ('type' in resetData && resetData.type === 'authenticated') {
          ({ access_token: accessToken, refresh_token: refreshToken, user: loginUser } = resetData.tokens);
        } else if ('type' in resetData && resetData.type === 'challenge') {
          // MFA challenge returned after password reset — mirror the normal login flow
          const challengeResp = resetData as AuthLoginChallenge;
          this.challengeData = challengeResp.challenge;
          if (
            challengeResp.challenge.challengeName === 'SOFTWARE_TOKEN_MFA' ||
            challengeResp.challenge.challengeName === 'EMAIL_OTP'
          ) {
            this.mfaChallengeType = challengeResp.challenge.challengeName as 'SOFTWARE_TOKEN_MFA' | 'EMAIL_OTP';
          }
          if (challengeResp.challenge.challengeName === 'SELECT_MFA_TYPE') {
            this.mfaSetupAccessToken = challengeResp.access_token ?? null;
            if (challengeResp.access_token && challengeResp.refresh_token && challengeResp.user) {
              await this._finishLogin({
                accessToken: challengeResp.access_token,
                refreshToken: challengeResp.refresh_token,
                loginUser: challengeResp.user,
                fallbackEmail: this.pendingEmail ?? challengeResp.user.username,
              });
            }
            this.mfaSetupRequired = true;
          }
          if (challengeResp.challenge.challengeName === 'MFA_SETUP' || challengeResp.mfaSetupRequired) {
            this.mfaSetupRequired = true;
          }
          auditStatus = 'SUCCESS';
          return false;
        } else if ('access_token' in resetData) {
          ({ access_token: accessToken, refresh_token: refreshToken, user: loginUser } = resetData as AuthResetPasswordResponse);
        } else {
          throw new Error('Unexpected response format after password reset');
        }

        this.challengeData = null;

        const success = await this._finishLogin({
          accessToken,
          refreshToken,
          loginUser,
          fallbackEmail: this.pendingEmail ?? loginUser.username,
        });

        if (success) {
          auditStatus = 'SUCCESS';
          auditResourceId = this.staffId ?? undefined;
          this.mfaSetupRequired = 'mfaSetupRequired' in resetData && (resetData as { mfaSetupRequired?: boolean }).mfaSetupRequired === true;
        } else {
          auditErrorCode = 'SET_PASSWORD_FINISH_LOGIN_FAILED';
          auditErrorMessage = this.error ?? 'Password set but post-login flow failed';
        }

        return success;
      } catch (err) {
        // SESSION_EXPIRED: challenge session expired (Cognito TTL = 3 min).
        // Clear stale challenge data so the view can redirect to login.
        if (isAxiosError(err) && err.response?.status === 401) {
          const code = (err.response.data as { error?: { code?: string } } | undefined)?.error?.code;
          if (code === 'SESSION_EXPIRED') {
            this.challengeData = null;
            this.error = (err.response.data as { error?: { message?: string } } | undefined)?.error?.message ?? 'Session expired. Please log in again.';
            auditStatus = 'FAILURE';
            auditErrorCode = 'SESSION_EXPIRED';
            auditErrorMessage = this.error;
            return false;
          }
        }
        this.error = getApiErrorMessage(err, 'Password reset failed');
        auditStatus = 'FAILURE';
        auditErrorCode = 'SET_PASSWORD_FAILED';
        auditErrorMessage = this.error ?? 'Password reset failed';
        if (isAxiosError(err)) {
          const apiError = (err.response?.data as { error?: unknown } | undefined)?.error;
          auditErrorCode = typeof apiError === 'string' ? apiError : err.code ?? 'SET_PASSWORD_FAILED';
        }
        return false;
      } finally {
        this.isLoading = false;
        try {
          logAuditEvent({
            eventCategory: 'AUTH',
            eventType: 'SET_PASSWORD',
            eventStatus: auditStatus,
            resourceType: 'users',
            resourceId: auditResourceId,
            isPhiAccess: false,
            oldValue: auditOldValue,
            newValue: auditNewValue,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch {
          // Audit must never block the flow
        }
      }
    },

    // ── MFA actions ────────────────────────────────────────────────────────

    async respondMfaChallenge(code: string): Promise<boolean> {
      if (!this.challengeData) {
        this.error = 'No active MFA challenge. Please log in again.';
        return false;
      }

      const { challengeName, session, challengeParameters } = this.challengeData;
      const username = challengeParameters?.USER_ID_FOR_SRP ?? this.pendingEmail ?? '';
      if (!username) {
        this.error = 'Session data is incomplete. Please log in again.';
        return false;
      }

      this.isLoading = true;
      this.error = null;

      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;

      try {
        const api = getApiClient();
        const response = await api.post<AuthResetPasswordResponse>(
          `${API_ROUTES.ONBOARDING}/auth/respond-challenge`,
          { challengeName, session, username, code },
        );

        this.challengeData = null;
        this.mfaChallengeType = null;

        const { access_token: accessToken, refresh_token: refreshToken, user: loginUser } = response.data;
        const ok = await this._finishLogin({ accessToken, refreshToken, loginUser, fallbackEmail: this.pendingEmail ?? loginUser.username });
        if (ok) {
          auditStatus = 'SUCCESS';
          // Persist mfaSetupRequired so the auth guard redirects to /mfa-setup when needed
          this.mfaSetupRequired = response.data.mfaSetupRequired === true;
        }
        return ok;
      } catch (err) {
        if (isAxiosError(err)) {
          const errCode = err.response?.data?.error?.code;
          if (errCode === 'SESSION_EXPIRED' || errCode === 'MFA_SESSION_EXPIRED') {
            this.challengeData = null;
            this.mfaChallengeType = null;
          }
          this.error = err.response?.data?.error?.message ?? err.message ?? 'Verification failed';
          auditErrorCode = errCode ?? 'MFA_CHALLENGE_FAILED';
          auditErrorMessage = this.error ?? undefined;
        } else {
          this.error = err instanceof Error ? err.message : 'Verification failed';
          auditErrorCode = 'MFA_CHALLENGE_FAILED';
          auditErrorMessage = this.error ?? undefined;
        }
        return false;
      } finally {
        this.isLoading = false;
        try {
          logAuditEvent({
            eventCategory: 'AUTH',
            eventType: 'MFA_CHALLENGE_VERIFY',
            eventStatus: auditStatus,
            resourceType: 'users',
            resourceId: this.staffId ?? undefined,
            isPhiAccess: false,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
            userId: this.staffId ?? undefined,
          });
        } catch {
          // Audit must never block the flow
        }
      }
    },

    async initiateTotpSetup(): Promise<boolean> {
      this.isLoading = true;
      this.error = null;
      this.totpQrData = null;

      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;

      try {
        const api = getApiClient();
        const response = await api.post<TotpInitiateResponse>(
          `${API_ROUTES.ONBOARDING}/mfa/setup/totp/initiate`,
        );
        this.totpQrData = response.data;
        auditStatus = 'SUCCESS';
        return true;
      } catch (err) {
        if (isAxiosError(err)) {
          auditErrorCode = err.response?.data?.error?.code ?? 'MFA_SETUP_TOTP_INITIATE_FAILED';
          this.error = err.response?.data?.error?.message ?? err.message;
        } else {
          this.error = err instanceof Error ? err.message : 'Failed to initiate TOTP setup';
          auditErrorCode = 'MFA_SETUP_TOTP_INITIATE_FAILED';
        }
        auditErrorMessage = this.error ?? undefined;
        return false;
      } finally {
        this.isLoading = false;
        try {
          logAuditEvent({
            eventCategory: 'AUTH',
            eventType: 'MFA_SETUP_TOTP_INITIATE',
            eventStatus: auditStatus,
            resourceType: 'users',
            resourceId: this.staffId ?? undefined,
            isPhiAccess: false,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
            userId: this.staffId ?? undefined,
          });
        } catch {
          // Audit must never block the flow
        }
      }
    },

    async verifyTotpSetup(code: string): Promise<boolean> {
      this.isLoading = true;
      this.error = null;

      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;

      try {
        const api = getApiClient();
        await api.post(`${API_ROUTES.ONBOARDING}/mfa/setup/totp/verify`, { code });
        this.totpQrData = null;
        this.mfaSetupRequired = false;
        this.challengeData = null;
        this.mfaSetupAccessToken = null;
        setAuthContext({
          staffId: this.staffId ?? undefined,
          hospitalId: this.hospitalId ?? undefined,
          tenantId: this.tenantId ?? undefined,
        });
        auditStatus = 'SUCCESS';
        return true;
      } catch (err) {
        if (isAxiosError(err)) {
          const errCode = err.response?.data?.error?.code;
          if (errCode === 'TOTP_SETUP_FAILED') this.totpQrData = null;
          this.error = err.response?.data?.error?.message ?? err.message;
          auditErrorCode = errCode ?? 'MFA_SETUP_TOTP_VERIFY_FAILED';
        } else {
          this.error = err instanceof Error ? err.message : 'TOTP verification failed';
          auditErrorCode = 'MFA_SETUP_TOTP_VERIFY_FAILED';
        }
        auditErrorMessage = this.error ?? undefined;
        return false;
      } finally {
        this.isLoading = false;
        try {
          logAuditEvent({
            eventCategory: 'AUTH',
            eventType: 'MFA_SETUP_TOTP_VERIFY',
            eventStatus: auditStatus,
            resourceType: 'users',
            resourceId: this.staffId ?? undefined,
            isPhiAccess: false,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
            userId: this.staffId ?? undefined,
          });
        } catch {
          // Audit must never block the flow
        }
      }
    },

    async setupEmailOtp(): Promise<boolean> {
      this.isLoading = true;
      this.error = null;

      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;

      try {
        const api = getApiClient();
        await api.post(`${API_ROUTES.ONBOARDING}/mfa/setup/email/initiate`);
        this.mfaSetupRequired = false;
        auditStatus = 'SUCCESS';
        return true;
      } catch (err) {
        if (isAxiosError(err)) {
          auditErrorCode = err.response?.data?.error?.code ?? 'MFA_SETUP_EMAIL_INITIATE_FAILED';
          this.error = err.response?.data?.error?.message ?? err.message;
        } else {
          this.error = err instanceof Error ? err.message : 'Failed to send email OTP';
          auditErrorCode = 'MFA_SETUP_EMAIL_INITIATE_FAILED';
        }
        auditErrorMessage = this.error ?? undefined;
        return false;
      } finally {
        this.isLoading = false;
        try {
          logAuditEvent({
            eventCategory: 'AUTH',
            eventType: 'MFA_SETUP_EMAIL_INITIATE',
            eventStatus: auditStatus,
            resourceType: 'users',
            resourceId: this.staffId ?? undefined,
            isPhiAccess: false,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
            userId: this.staffId ?? undefined,
          });
        } catch {
          // Audit must never block the flow
        }
      }
    },

    async verifyEmailOtp(code: string): Promise<boolean> {
      this.isLoading = true;
      this.error = null;

      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;

      try {
        const api = getApiClient();
        await api.post(`${API_ROUTES.ONBOARDING}/mfa/setup/email/verify`, { code });
        this.mfaSetupRequired = false;
        this.mfaSetupAccessToken = null;
        setAuthContext({
          staffId: this.staffId ?? undefined,
          hospitalId: this.hospitalId ?? undefined,
          tenantId: this.tenantId ?? undefined,
        });
        auditStatus = 'SUCCESS';
        return true;
      } catch (err) {
        if (isAxiosError(err)) {
          auditErrorCode = err.response?.data?.error?.code ?? 'MFA_SETUP_EMAIL_VERIFY_FAILED';
          this.error = err.response?.data?.error?.message ?? err.message;
        } else {
          this.error = err instanceof Error ? err.message : 'Email verification failed';
          auditErrorCode = 'MFA_SETUP_EMAIL_VERIFY_FAILED';
        }
        auditErrorMessage = this.error ?? undefined;
        return false;
      } finally {
        this.isLoading = false;
        try {
          logAuditEvent({
            eventCategory: 'AUTH',
            eventType: 'MFA_SETUP_EMAIL_VERIFY',
            eventStatus: auditStatus,
            resourceType: 'users',
            resourceId: this.staffId ?? undefined,
            isPhiAccess: false,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
            userId: this.staffId ?? undefined,
          });
        } catch {
          // Audit must never block the flow
        }
      }
    },

    async getMfaStatus(): Promise<MfaStatus | null> {
      try {
        const api = getApiClient();
        const response = await api.get<MfaStatus>(`${API_ROUTES.ONBOARDING}/mfa/status`);
        return response.data;
      } catch {
        return null;
      }
    },

    async fetchMfaMethods(): Promise<('authenticator' | 'email')[]> {
      try {
        const api = getApiClient();
        const response = await api.get<{ methods: string[] }>(`${API_ROUTES.ONBOARDING}/mfa/methods`);
        const methods: string[] = response.data?.methods ?? [];
        const mapped: ('authenticator' | 'email')[] = [];
        if (methods.includes('TOTP')) mapped.push('authenticator');
        if (methods.includes('EMAIL_OTP')) mapped.push('email');
        return mapped.length > 0 ? mapped : ['authenticator', 'email'];
      } catch {
        return ['authenticator', 'email'];
      }
    },

    // ── In-auth MFA setup (Required MFA pools — MFA_SETUP challenge, no token yet) ──

    async initiateInAuthTotpSetup(): Promise<boolean> {
      if (!this.challengeData) return false;
      this.isLoading = true;
      this.error = null;
      this.totpQrData = null;
      const session = this.challengeData.session;
      const username = this.challengeData.challengeParameters?.USER_ID_FOR_SRP ?? this.pendingEmail ?? '';
      try {
        const api = getApiClient();
        const response = await api.post<{ session: string; secretCode: string; otpauthUri: string; qrCodeDataUrl: string }>(
          `${API_ROUTES.ONBOARDING}/auth/mfa-setup/totp/initiate`,
          { session, username, loginType: 'default' },
        );
        this.challengeData = { ...this.challengeData, session: response.data.session };
        this.totpQrData = { qrCodeDataUrl: response.data.qrCodeDataUrl, otpauthUri: response.data.otpauthUri };
        return true;
      } catch (err) {
        this.error = isAxiosError(err) ? (err.response?.data?.error?.message ?? err.message) : (err instanceof Error ? err.message : 'Failed to initiate TOTP setup');
        return false;
      } finally { this.isLoading = false; }
    },

    async completeInAuthTotpSetup(code: string): Promise<boolean> {
      if (!this.challengeData) return false;
      this.isLoading = true;
      this.error = null;
      const session = this.challengeData.session;
      const username = this.challengeData.challengeParameters?.USER_ID_FOR_SRP ?? this.pendingEmail ?? '';
      try {
        const api = getApiClient();
        const response = await api.post<AuthLoginResponse>(
          `${API_ROUTES.ONBOARDING}/auth/mfa-setup/totp/complete`,
          { session, username, code, loginType: 'default' },
        );
        const data = response.data;
        if ('type' in data && data.type === 'authenticated') {
          this.challengeData = null;
          this.totpQrData = null;
          const { access_token: accessToken, refresh_token: refreshToken, user: loginUser } = data.tokens;
          const ok = await this._finishLogin({ accessToken, refreshToken, loginUser, fallbackEmail: loginUser.username });
          if (ok) { this.mfaSetupRequired = false; this.mfaSetupAccessToken = null; }
          return ok;
        }
        if ('type' in data && data.type === 'challenge') {
          this.challengeData = data.challenge;
          if (data.challenge.challengeName === 'EMAIL_OTP') this.mfaChallengeType = 'EMAIL_OTP';
          return false;
        }
        return false;
      } catch (err) {
        if (isAxiosError(err)) {
          if (err.response?.data?.error?.code === 'TOTP_SETUP_FAILED') this.totpQrData = null;
          this.error = err.response?.data?.error?.message ?? err.message;
        } else { this.error = err instanceof Error ? err.message : 'TOTP verification failed'; }
        return false;
      } finally { this.isLoading = false; }
    },

    async completeInAuthEmailSetup(): Promise<boolean> {
      if (!this.challengeData) return false;
      this.isLoading = true;
      this.error = null;
      const session = this.challengeData.session;
      const username = this.challengeData.challengeParameters?.USER_ID_FOR_SRP ?? this.pendingEmail ?? '';
      try {
        const api = getApiClient();
        const response = await api.post<AuthLoginResponse>(
          `${API_ROUTES.ONBOARDING}/auth/mfa-setup/email/complete`,
          { session, username, loginType: 'default' },
        );
        const data = response.data;
        if ('type' in data && data.type === 'challenge' && data.challenge.challengeName === 'EMAIL_OTP') {
          this.challengeData = data.challenge;
          this.mfaChallengeType = 'EMAIL_OTP';
          return true;
        }
        if ('type' in data && data.type === 'authenticated') {
          this.challengeData = null;
          const { access_token: accessToken, refresh_token: refreshToken, user: loginUser } = data.tokens;
          const ok = await this._finishLogin({ accessToken, refreshToken, loginUser, fallbackEmail: loginUser.username });
          if (ok) { this.mfaSetupRequired = false; }
          return ok;
        }
        return false;
      } catch (err) {
        this.error = isAxiosError(err) ? (err.response?.data?.error?.message ?? err.message) : (err instanceof Error ? err.message : 'Failed to enable Email OTP');
        return false;
      } finally { this.isLoading = false; }
    },

    /**
     * @param options.navigate - When false, skips the `/login` push at the end
     *   (default true). Used by authGuard when it is itself the navigation
     *   that triggered the logout (e.g. a direct /login visit while a session
     *   already exists) — pushing here would start a second, redundant
     *   navigation on top of the guard's own `next()`.
     * @param options.clearFirst - When true, wipes the local session BEFORE the
     *   server call (default false). Used by ActiveSessionView so the previous
     *   user's tokens are gone from the device immediately instead of after the
     *   request settles. The captured tokens are still sent to the server.
     * @param options.timeoutMs - Per-request timeout for the server call, so a
     *   slow or unreachable API can't hold the caller up for the default 30 s.
     */
    async logout(options: { navigate?: boolean; clearFirst?: boolean; timeoutMs?: number } = {}): Promise<void> {
      const { navigate = true, clearFirst = false, timeoutMs } = options;
      let auditStatus: 'SUCCESS' | 'FAILURE' = 'SUCCESS';
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      const auditResourceId = this.staffId ?? undefined;
      const auditOldValue = { isAuthenticated: true };
      const auditNewValue = { isAuthenticated: false };

      // Capture everything the server call and the audit event need — with
      // clearFirst the store is already empty by the time they run.
      const accessToken = this.accessToken;
      const auditUserId = this.staffId ?? undefined;

      const clearLocalSession = (): void => {
        // Clear all browser storage for this app session on logout.
        localStorage.clear();
        sessionStorage.clear();

        // clearAuth() resets in-memory auth state and mutable auth context
        this.clearAuth();
      };

      if (clearFirst) clearLocalSession();

      try {
        const api = getApiClient();
        // PHX-1180: omit access_token entirely when absent (e.g. post-reload) —
        // the schema only accepts a non-empty string or no field at all; the
        // backend falls back to the httpOnly cookie to clear the server session.
        await api.post(
          `${API_ROUTES.ONBOARDING}/auth/logout`,
          accessToken ? { access_token: accessToken } : {},
          // Only pass a config object when a timeout was asked for.
          ...(timeoutMs === undefined ? [] : [{ timeout: timeoutMs }]),
        );
      } catch (err) {
        auditStatus = 'FAILURE';
        auditErrorCode = 'LOGOUT_FAILED';
        auditErrorMessage = err instanceof Error ? err.message : 'Logout error';
        if (isAxiosError(err)) {
          const apiError = (err.response?.data as { error?: unknown } | undefined)?.error;
          auditErrorCode = typeof apiError === 'string' ? apiError : err.code ?? 'LOGOUT_FAILED';
        }
        console.error('Logout error:', err);
      } finally {
        try {
          // The identity was captured above — logAuditEvent is fire-and-forget and the
          // axios token interceptor runs asynchronously (microtask), so by the time
          // it executes the pinia-auth localStorage entry is already cleared by
          // clearAuth(). Passing userId explicitly lets the backend insert the row
          // even when the JWT is absent (optionalCognitoAuth falls back to body fields).
          logAuditEvent({
            eventCategory: 'AUTH',
            eventType: 'LOGOUT',
            eventStatus: auditStatus,
            resourceType: 'users',
            resourceId: auditResourceId,
            isPhiAccess: false,
            oldValue: auditOldValue,
            newValue: auditNewValue,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
            userId: auditUserId,
          });
        } catch {
          // Audit must never block the flow
        }

        if (!clearFirst) clearLocalSession();

        if (navigate) router.push('/login');
      }
    },

    /**
     * Exchange the current refresh token for a new token pair.
     *
     * POST /onboarding/api/v1/auth/refresh
     */
    async refreshAccessToken(): Promise<boolean> {
      // SV-563: honour a 429's Retry-After for every caller, without touching the scheduled
      // retry (it fires at that time anyway). The user stays signed in meanwhile; no audit
      // event, since no request is made.
      if (Date.now() < _refreshNotBefore) return false;

      if (_refreshPromise) return _refreshPromise;

      _refreshPromise = this._doRefresh();
      try {
        return await _refreshPromise;
      } finally {
        _refreshPromise = null;
      }
    },

    async _doRefresh(): Promise<boolean> {
      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      const auditResourceId = this.staffId ?? undefined;
      const previousRefreshToken = this.refreshToken;
      // SV-563: in-memory token expiry, or the session-check expiry after a page reload
      const previousAccessTokenExp = this.sessionExp;
      let auditOldValue: unknown | undefined;
      let auditNewValue: unknown | undefined;

      // Prefer this.username (the cognito:username used at login) for the SECRET_HASH
      // computation on the backend. Cognito requires the same username value that was
      // used during login — this.staffId is the Cognito `sub`, which only coincides
      // with the pool Username for non-federated users; SSO/control-plane users have
      // a provider-prefixed Username distinct from sub, so sending staffId there makes
      // Cognito reject the refresh token as invalid. Fall back to staffId only if
      // username is absent (e.g. migrated sessions from before this fix).
      const cognitoUsername = this.username ?? this.staffId;

      if (!cognitoUsername) {
        auditStatus = 'FAILURE';
        auditErrorCode = 'REFRESH_TOKEN_PRECONDITION_FAILED';
        auditErrorMessage = 'Missing staffId';
        this.clearAuth();
        try {
          logAuditEvent({
            eventCategory: 'AUTH',
            eventType: 'REFRESH_TOKEN',
            eventStatus: auditStatus,
            resourceType: 'users',
            resourceId: auditResourceId,
            isPhiAccess: false,
            oldValue: undefined,
            newValue: undefined,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch {
          // Audit must never block the flow
        }
        router.push('/login');
        return false;
      }

      try {
        const api = getApiClient();
        // PHX-1180: refreshToken is no longer persisted, so it's null after a
        // page reload — the backend falls back to the httpOnly tms_refresh_token cookie
        // in that case. Still send it when present (e.g. immediately post-login).
        const body: { username: string; refresh_token?: string } = { username: cognitoUsername };
        if (this.refreshToken) {
          body.refresh_token = this.refreshToken;
        }
        const { data } = await api.post<{ access_token: string; refresh_token?: string }>(
          `${API_ROUTES.ONBOARDING}/auth/refresh`,
          body,
        );

        this.accessToken = data.access_token;
        if (data.refresh_token) {
          this.refreshToken = data.refresh_token;
        }

        const currentAccessTokenExp = decodeJwtExp(this.accessToken);
        const refreshTokenRotated = Boolean(data.refresh_token && data.refresh_token !== previousRefreshToken);

        auditOldValue = {
          accessTokenExp: previousAccessTokenExp,
          hasRefreshToken: Boolean(previousRefreshToken),
        };
        auditNewValue = {
          accessTokenExp: currentAccessTokenExp,
          hasRefreshToken: Boolean(this.refreshToken),
          refreshTokenRotated,
        };
        auditStatus = 'SUCCESS';
        this._clearSessionRetry();
        _refreshNotBefore = 0;
        this._scheduleProactiveRefresh();

        return true;
      } catch (err) {
        auditStatus = 'FAILURE';
        auditErrorCode = 'REFRESH_TOKEN_FAILED';
        auditErrorMessage = err instanceof Error ? err.message : 'Refresh token failed';
        if (isAxiosError(err)) {
          const apiError = (err.response?.data as { error?: unknown } | undefined)?.error;
          auditErrorCode = typeof apiError === 'string' ? apiError : err.code ?? 'REFRESH_TOKEN_FAILED';
        }
        // SV-563: only an invalid session logs the user out. A 429 (rate limit), 5xx or
        // network error is transient — keep the user signed in and retry later.
        if (isAxiosError(err) && SESSION_INVALID_STATUSES.has(err.response?.status ?? 0)) {
          this.clearAuth();
          router.push('/login');
        } else {
          // A 429 says exactly how long to wait — hold every refresh until then (5xx/network
          // get no cooldown: a deploy can recover within seconds).
          const waitMs = retryAfterMs(err);
          if (waitMs !== null) _refreshNotBefore = Date.now() + waitMs;
          // Retry via the timer path so it adopts another tab's refresh instead of repeating it.
          // Record whether this failure will be retried, so the audit trail shows recovery.
          const scheduled = this._scheduleSessionRetry(err, () => this._onRefreshTimer());
          auditNewValue = scheduled ?? { retriesExhausted: true };
        }
        return false;
      } finally {
        try {
          logAuditEvent({
            eventCategory: 'AUTH',
            eventType: 'REFRESH_TOKEN',
            eventStatus: auditStatus,
            resourceType: 'users',
            resourceId: auditResourceId,
            isPhiAccess: false,
            oldValue: auditOldValue,
            newValue: auditNewValue,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch {
          // Audit must never block the flow
        }
      }
    },

    /**
     * SV-563: confirm the httpOnly access cookie is still valid on page reload, without
     * spending a rate-limited /auth/refresh call.
     *
     * Fast path: the readable `tms_session_exp` marker cookie already says when the access
     * cookie expires — no request needed. Otherwise (marker missing):
     *
     * Endpoint: GET /onboarding/api/v1/auth/session-check → { expiresAt, username }
     *
     *   • 200 → remember the expiry and schedule the proactive refresh for ~60 s before it.
     *   • 401 → access cookie expired/missing — fall back to a single cookie-based refresh.
     *   • 429 / 5xx / network → keep the user signed in and retry later.
     */
    async validateSession(): Promise<boolean> {
      const markerExp = readSessionExpCookie();
      if (markerExp !== null) {
        this.sessionExpiresAt = markerExp;
        this._clearSessionRetry();
        this._scheduleProactiveRefresh();
        return true;
      }

      try {
        const api = getApiClient();
        const { data } = await api.get<{ expiresAt: number; username: string }>(
          `${API_ROUTES.ONBOARDING}/auth/session-check`,
        );

        this.sessionExpiresAt = data.expiresAt;
        this._clearSessionRetry();
        this._scheduleProactiveRefresh();
        return true;
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 401) {
          return this.refreshAccessToken();
        }
        this._scheduleSessionRetry(err, () => this.validateSession());
        return true;
      }
    },

    /**
     * SV-563: schedule `retry` after a transient auth failure, per getSessionRetryDelayMs().
     * Logs status/attempt/delay only — never tokens or usernames.
     * Returns what was scheduled, or `null` when retries are exhausted.
     */
    _scheduleSessionRetry(err: unknown, retry: () => Promise<boolean>): ScheduledSessionRetry {
      if (_sessionRetryTimerId) clearTimeout(_sessionRetryTimerId);
      _sessionRetryTimerId = null;

      const failure = describeAuthFailure(err);
      const delayMs = getSessionRetryDelayMs(err, _sessionRetryAttempt);
      if (delayMs === null) {
        console.warn(`[auth.store] transient auth failure (${failure}) — retries exhausted; next 401 will refresh`);
        // Reset so a later, unrelated failure gets a fresh set of attempts. Nothing is
        // scheduled here, so this cannot loop.
        _sessionRetryAttempt = 0;
        return null;
      }

      _sessionRetryAttempt += 1;
      console.warn(
        `[auth.store] transient auth failure (${failure}) — retry ${_sessionRetryAttempt}/${SESSION_RETRY_DELAYS_MS.length} in ${Math.round(delayMs / 1000)}s`,
      );
      _sessionRetryTimerId = setTimeout(() => {
        _sessionRetryTimerId = null;
        void retry();
      }, delayMs);
      return { retryAttempt: _sessionRetryAttempt, retryInMs: delayMs };
    },

    /** SV-563: cancel any pending transient-failure retry and reset the attempt counter. */
    _clearSessionRetry() {
      if (_sessionRetryTimerId) clearTimeout(_sessionRetryTimerId);
      _sessionRetryTimerId = null;
      _sessionRetryAttempt = 0;
    },

    clearAuth() {
      this._clearRefreshTimer();
      this._clearSessionRetry();
      _refreshNotBefore = 0;
      this.accessToken = null;
      this.refreshToken = null;
      this.sessionExpiresAt = null;
      _sessionMarkerSeen = false;
      this.user = null;
      this.userDetails = null;
      this.username = null;
      this.staffId = null;
      this.hospitalId = null;
      this.tenantId = null;
      this.roles = null;
      this.parsedRoles = [];
      this.defaultLanguage = null;
      this.timeZone = null;
      this.challengeData = null;
      this.mfaSetupRequired = false;
      this.mfaChallengeType = null;
      this.totpQrData = null;
      this.mfaSetupAccessToken = null;
      this.pendingEmail = null;
      this.error = null;

      // Reset permissions store on logout (async, best-effort)
      import('@/stores/permissions.store').then(({ usePermissionsStore }) => {
        try {
          usePermissionsStore().reset();
        } catch {
          // Non-fatal: store cleanup on logout
        }
      }).catch(() => {
        // Non-fatal: module import failed
      });

      // Clear standalone localStorage token keys (set by storeTokens() during SSO flow)
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('idToken');
      clearAuthContext();

      // Reset the persisted idle-activity timestamp so the next login isn't
      // immediately flagged as idle-expired by a stale value.
      clearLastActivityTimestamp();
    },

    /**
     * Clears auth state and redirects to /login without calling the logout API.
     * Used when the session is already known to be invalid server-side (e.g. a
     * refresh-token retry that still 401s) — hitting /auth/logout would just 401 again.
     */
    forceLogout() {
      this.clearAuth();
      router.push('/login');
    },

    hasRole(role: string): boolean {
      return this.userRole === role;
    },

    hasAnyRole(roles: string[]): boolean {
      return roles.some((role) => this.hasRole(role));
    },

    /**
     * Hydrate the auth store from SSO callback data.
     * Called by useOidc.handleCallback() after a successful token exchange.
     */
    setAuth(
      tokens: { accessToken: string; refreshToken?: string | null },
      userData: BaseUser,
      context?: { staffId?: string; hospitalId?: string; tenantId?: string; roles?: string; username?: string },
      details?: UserDetailsResponse | null,
    ) {
      this.accessToken = tokens.accessToken;
      this.refreshToken = tokens.refreshToken ?? null;
      this.user = userData;
      this.userDetails = details ?? null;

      // Persist auth context (staffId, hospitalId, tenantId, roles, username)
      if (context) {
        this.staffId = context.staffId ?? null;
        this.hospitalId = context.hospitalId ?? null;
        this.tenantId = context.tenantId ?? null;
        this.roles = context.roles ?? null;
        // Store the cognito:username so token refresh uses the correct value for SECRET_HASH
        if (context.username) {
          this.username = context.username;
        }

        // Parse roles (JSON array string OR comma-separated) → string[]
        if (context.roles) {
          try {
            const parsed = JSON.parse(context.roles);
            if (Array.isArray(parsed)) {
              this.parsedRoles = parsed.filter((r): r is string => typeof r === 'string');
            } else {
              this.parsedRoles = [];
            }
          } catch {
            this.parsedRoles = [];
          }
        } else {
          this.parsedRoles = [];
        }

        // Sync mutable config values
        setAuthContext({
          staffId: context.staffId,
          hospitalId: context.hospitalId,
          tenantId: context.tenantId,
        });
      }

      // Schedule proactive refresh ~60 s before the access token expires
      this._scheduleProactiveRefresh();

      // ── Fetch role-module-matrix permissions (fire-and-forget) ────────
      // Permissions are used to control module visibility in the UI.
      // This is non-blocking: if the fetch fails, the app continues to work.
      import('@/stores/permissions.store').then(({ usePermissionsStore }) => {
        usePermissionsStore().fetchPermissions().catch(() => {
          // Non-fatal: permission-gated UI will update when fetch succeeds
        });
      }).catch(() => {
        // Silently ignore if permissions fetch couldn't be triggered
      });

    },

    _scheduleProactiveRefresh() {
      this._clearRefreshTimer();

      // SV-563: in-memory token expiry, or the session-check expiry after a page reload
      const exp = this.sessionExp;
      if (!exp) return;

      const nowSec = Math.floor(Date.now() / 1000);
      // Refresh 60–90 s before expiry, minimum 10 s from now. SV-563: the random spread stops
      // tabs that share an expiry (via the marker) from all refreshing in the same second —
      // the first one refreshes and the others adopt its marker. Never fire inside a 429
      // cooldown (+1 s margin): refreshAccessToken() would reject it and nothing would retry.
      const delaySec = Math.max(
        exp - nowSec - 60 - Math.random() * REFRESH_SPREAD_SEC,
        10,
        (_refreshNotBefore - Date.now()) / 1000 + 1,
      );

      _refreshTimerId = setTimeout(() => {
        void this._onRefreshTimer();
      }, delaySec * 1000);
    },

    /**
     * SV-563: proactive-refresh timer callback. If another tab already refreshed (the shared
     * marker cookie moved forward), adopt its expiry instead of spending a /auth/refresh call.
     */
    async _onRefreshTimer(): Promise<boolean> {
      if (this._adoptNewerSessionMarker()) return true;
      return this.refreshAccessToken();
    },

    /**
     * SV-563: if the marker cookie shows a later expiry than this tab knows (another tab
     * refreshed), adopt it and reschedule. Returns true when adopted.
     */
    _adoptNewerSessionMarker(): boolean {
      const markerExp = readSessionExpCookie();
      const knownExp = this.sessionExp;
      if (markerExp === null) return false;
      if (knownExp !== null && markerExp <= knownExp + MARKER_NEWER_THRESHOLD_SEC) return false;

      // This tab's in-memory token is now older than the cookie — drop it so sessionExp and
      // logout() follow the fresh cookie session instead.
      this.accessToken = null;
      this.sessionExpiresAt = markerExp;
      // Another tab's refresh succeeded — a pending transient-failure retry would only waste
      // one of the limited refreshes.
      this._clearSessionRetry();
      this._scheduleProactiveRefresh();
      return true;
    },

    /**
     * SV-563: when the tab becomes visible again, adopt a newer marker (another tab refreshed),
     * or re-check the session if the marker has disappeared (logged out in another tab, or it
     * expired while the laptop slept). Only treats a missing marker as meaningful once this
     * page has seen one, so environments without the marker don't re-check on every focus.
     */
    _onVisibilityChange() {
      if (document.visibilityState !== 'visible' || !this.isAuthenticated) return;
      if (this._adoptNewerSessionMarker()) return;
      if (_sessionMarkerSeen && this.hasActiveSession && readSessionExpCookie() === null) {
        void this.validateSession();
      }
    },

    /** SV-563: register the visibilitychange listener once per page. */
    _watchSessionMarker() {
      if (_visibilityListenerRegistered) return;
      _visibilityListenerRegistered = true;
      // Resolve the store at event time (not a captured `this`) so it's always the active one.
      document.addEventListener('visibilitychange', () => useAuthStore()._onVisibilityChange());
    },

    _clearRefreshTimer() {
      if (_refreshTimerId) {
        clearTimeout(_refreshTimerId);
        _refreshTimerId = null;
      }
    },

    /**
     * Re-schedule the proactive refresh timer after a page reload.
     * Call once from main.ts after setupPinia().
     */
    initRefreshTimer() {
      // PHX-1180: one-time migration — purge legacy standalone OIDC token keys
      // that older app versions wrote directly to localStorage (bypassing the
      // pinia-persist allow-list), so they don't linger for existing sessions.
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('idToken');

      this._watchSessionMarker();

      if (this.accessToken) {
        this._scheduleProactiveRefresh();
      } else if (this.staffId) {
        // A laptop locked/asleep (or the tab fully closed) across a gap longer than the idle
        // timeout means the in-memory idle timer in App.vue never got a chance to fire — force
        // a fresh login instead of silently resuming via the refresh cookie.
        if (hasIdleTimeoutElapsed()) {
          // Go through the real logout() (not just clearAuth()) so the httpOnly refresh
          // cookie is actually invalidated server-side via the cookie-fallback /auth/logout
          // call — otherwise the "logged out" browser would still hold a valid refresh
          // credential despite appearing logged out locally.
          void this.logout();
        } else {
          // PHX-1180: accessToken/refreshToken are no longer persisted, so a page
          // reload leaves them null in memory even though the httpOnly cookies may
          // still be valid. SV-563: validate the access cookie instead of calling the
          // rate-limited /auth/refresh on every reload — validateSession() only falls
          // back to a refresh when the access cookie has actually expired.
          void this.validateSession();
        }
      }

      if (this.staffId || this.hospitalId || this.tenantId) {
        setAuthContext({
          staffId: this.staffId ?? undefined,
          hospitalId: this.hospitalId ?? undefined,
          tenantId: this.tenantId ?? undefined,
        });
      }
    },
  },

  persist: {
    paths: [
      'user',
      'userDetails',
      // PHX-1180: accessToken/refreshToken are intentionally NOT persisted —
      // the real session now lives in an httpOnly cookie; these fields are
      // in-memory-only for the lifetime of the tab.
      'username',
      'staffId',
      'hospitalId',
      'tenantId',
      'roles',
      'parsedRoles',
      'defaultLanguage',
      'timeZone',
      'challengeData',
      'mfaSetupRequired',
    ],
  },
});
